// Acciones sobre el vendedor: editar, desactivar/activar y eliminar; las dos últimas con su confirmación.
import { useState } from 'react';
import { Power, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button, ConfirmDialog, Input } from '@/kit';
import {
    useChangeRetailUserStatus,
    useDeleteRetailUser,
} from '@/domains/retail/negocios/api/useRetailOrg';
import {
    toRosterWriteError,
    useInvalidateRoster,
    useRosterAbilities,
} from '../../lib/rosterWrites';
import { isInactive, type SellerRow } from '../lib/sellersTable';
import { EditSellerButton } from './EditSellerDrawer';
import { useIntegrationLock } from '../../integration/components/IntegrationLock';

/** Palabra que habilita el borrado si no se escribe el nombre. */
export const DELETE_CONFIRM_WORD = 'ELIMINAR';

const normalized = (value: string) => value.trim().toLocaleLowerCase('es-MX');

/** Confirma el borrado si se escribió el nombre del vendedor o la palabra ELIMINAR. */
export function isDeleteConfirmed(typed: string, sellerName: string): boolean {
    return typed.trim() === DELETE_CONFIRM_WORD || normalized(typed) === normalized(sellerName);
}

type OpenDialog = 'status' | 'delete' | null;

const HAS_OTHER_ACCESS = 'SELLER_HAS_OTHER_ACCESS';

function ErrorNote({ message }: { message: string | null }) {
    if (message === null) return null;
    return (
        <p role="alert" className="text-[12px] text-danger">
            {message}
        </p>
    );
}

/** Sin cadena o sin permisos no se pinta nada. */
export function SellerAccessActions({
    row,
    retailerId,
}: {
    row: SellerRow;
    retailerId: string | null;
}) {
    const { canChangeUserStatus, canDeleteUser, canUpdateUser } = useRosterAbilities();
    if (retailerId === null || (!canChangeUserStatus && !canDeleteUser && !canUpdateUser)) {
        return null;
    }
    return (
        <SellerAccessButtons
            key={row.sellerId}
            row={row}
            retailerId={retailerId}
            canChangeStatus={canChangeUserStatus && row.status !== null}
            canDelete={canDeleteUser}
        />
    );
}

function SellerAccessButtons({
    row,
    retailerId,
    canChangeStatus,
    canDelete,
}: {
    row: SellerRow;
    retailerId: string;
    canChangeStatus: boolean;
    canDelete: boolean;
}) {
    const changeStatus = useChangeRetailUserStatus(retailerId);
    const deleteUser = useDeleteRetailUser(retailerId);
    const lock = useIntegrationLock();
    const invalidateRoster = useInvalidateRoster();
    const [open, setOpen] = useState<OpenDialog>(null);
    const [error, setError] = useState<string | null>(null);
    const [typed, setTyped] = useState('');
    const activating = isInactive(row);

    const openDialog = (dialog: OpenDialog) => {
        setError(null);
        setTyped('');
        setOpen(dialog);
    };

    const run = async (action: () => Promise<unknown>, success: string) => {
        setError(null);
        try {
            await action();
        } catch (failure) {
            const mapped = toRosterWriteError(failure);
            // Con otros accesos fuera de la cadena no hay nada que reintentar: se avisa y se cierra.
            if (mapped.code === HAS_OTHER_ACCESS) {
                toast.error(mapped.message);
                setOpen(null);
                return;
            }
            setError(mapped.message);
            return;
        }
        toast.success(success);
        setOpen(null);
        await invalidateRoster();
    };

    return (
        <div className="ml-auto flex items-center gap-1.5">
            <EditSellerButton row={row} retailerId={retailerId} />
            {canChangeStatus ? (
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-[11px]"
                    leftIcon={<Power className="size-3" aria-hidden />}
                    disabled={lock.locked}
                    title={lock.reason}
                    onClick={() => {
                        openDialog('status');
                    }}
                >
                    {activating ? 'Activar' : 'Desactivar'}
                </Button>
            ) : null}
            {canDelete ? (
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-[11px] text-danger"
                    leftIcon={<Trash2 className="size-3" aria-hidden />}
                    disabled={lock.locked}
                    title={lock.reason}
                    onClick={() => {
                        openDialog('delete');
                    }}
                >
                    Eliminar
                </Button>
            ) : null}

            <ConfirmDialog
                open={open === 'status'}
                onOpenChange={(next) => {
                    if (!next) setOpen(null);
                }}
                title={activating ? `¿Activar a ${row.name}?` : `¿Desactivar a ${row.name}?`}
                description={
                    activating
                        ? 'Podrá volver a entrar al portal.'
                        : 'Ya no podrá entrar al portal hasta que lo actives.'
                }
                confirmLabel={activating ? 'Activar' : 'Desactivar'}
                destructive={!activating}
                loading={changeStatus.isPending}
                onConfirm={() => {
                    void run(
                        () =>
                            changeStatus.mutateAsync({
                                retailUserId: row.sellerId,
                                status: activating ? 'ACTIVE' : 'INACTIVE',
                            }),
                        activating ? 'Vendedor activado' : 'Vendedor desactivado',
                    );
                }}
            >
                <ErrorNote message={error} />
            </ConfirmDialog>

            <ConfirmDialog
                open={open === 'delete'}
                onOpenChange={(next) => {
                    if (!next) setOpen(null);
                }}
                title={`¿Eliminar a ${row.name}?`}
                description={`Esto borra a ${row.name} y su acceso para siempre. No se puede deshacer.`}
                confirmLabel="Eliminar"
                destructive
                loading={deleteUser.isPending}
                confirmDisabled={!isDeleteConfirmed(typed, row.name)}
                onConfirm={() => {
                    void run(() => deleteUser.mutateAsync(row.sellerId), 'Vendedor eliminado');
                }}
            >
                <Input
                    label={`Para confirmar, escribe su nombre o ${DELETE_CONFIRM_WORD}`}
                    autoComplete="off"
                    value={typed}
                    onChange={(event) => {
                        setTyped(event.target.value);
                    }}
                />
                <ErrorNote message={error} />
            </ConfirmDialog>
        </div>
    );
}
